import React from 'react';
import PropTypes from 'prop-types';
import styles from './ContentSkeleton.module.css';

/**
 * Premium editorial skeleton loader for text rendering and content viewing areas.
 * Designed with realistic typographic hierarchy, natural variable line widths,
 * and a smooth, low-contrast shimmer animation.
 */
const ContentSkeleton = ({
  className = '',
  style = {},
  maxWidth = '760px',
  compact = false,
  animate = true,
  ariaLabel = 'Loading content...',
}) => {
  const containerClasses = [
    styles.container,
    compact ? styles.compact : '',
    animate ? styles.animated : styles.static,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={containerClasses}
      style={{ maxWidth, ...style }}
      role="status"
      aria-busy="true"
      aria-label={ariaLabel}
    >
      {/* Screen-reader-only announcement */}
      <span className={styles.srOnly}>{ariaLabel}</span>

      {/* 1. Meta / Eyebrow line */}
      <div className={styles.metaRow}>
        <div className={`${styles.line} ${styles.eyebrow}`} />
        <div className={`${styles.line} ${styles.timestamp}`} />
      </div>

      {/* 2. Main Editorial Heading */}
      <div className={styles.headingGroup}>
        <div className={`${styles.line} ${styles.title}`} />
        <div className={`${styles.line} ${styles.subtitle}`} />
      </div>

      {/* 3. Subtle Section Divider */}
      <div className={styles.divider} />

      {/* 4. Lead / Introductory Paragraph */}
      <div className={styles.paragraph}>
        <div className={`${styles.line} ${styles.leadLine}`} style={{ width: '100%' }} />
        <div className={`${styles.line} ${styles.leadLine}`} style={{ width: '96%' }} />
        <div className={`${styles.line} ${styles.leadLine}`} style={{ width: '74%' }} />
      </div>

      {/* 5. Body Paragraph 1 (Natural prose rhythm) */}
      <div className={styles.paragraph}>
        <div className={styles.line} style={{ width: '100%' }} />
        <div className={styles.line} style={{ width: '98%' }} />
        <div className={styles.line} style={{ width: '92%' }} />
        <div className={styles.line} style={{ width: '84%' }} />
        <div className={styles.line} style={{ width: '48%' }} />
      </div>

      {/* 6. Mid-Content Subheading */}
      <div className={styles.subheadingGroup}>
        <div className={`${styles.line} ${styles.subheading}`} />
      </div>

      {/* 7. Body Paragraph 2 */}
      <div className={styles.paragraph}>
        <div className={styles.line} style={{ width: '100%' }} />
        <div className={styles.line} style={{ width: '95%' }} />
        <div className={styles.line} style={{ width: '91%' }} />
        <div className={styles.line} style={{ width: '62%' }} />
      </div>

      {/* 8. Callout / Code Quote Indented Block */}
      <div className={styles.calloutBlock}>
        <div className={styles.line} style={{ width: '88%' }} />
        <div className={styles.line} style={{ width: '94%' }} />
        <div className={styles.line} style={{ width: '56%' }} />
      </div>

      {/* 9. Concluding Paragraph */}
      <div className={styles.paragraph}>
        <div className={styles.line} style={{ width: '99%' }} />
        <div className={styles.line} style={{ width: '82%' }} />
        <div className={styles.line} style={{ width: '38%' }} />
      </div>
    </div>
  );
};

ContentSkeleton.propTypes = {
  /** Optional additional CSS class */
  className: PropTypes.string,
  /** Inline style overrides */
  style: PropTypes.object,
  /** Maximum content container width (e.g. '720px', '100%') */
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Tighter vertical rhythm mode */
  compact: PropTypes.bool,
  /** Enable or disable the shimmer sweep */
  animate: PropTypes.bool,
  /** Accessible label for screen readers */
  ariaLabel: PropTypes.string,
};

export default ContentSkeleton;